import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { PortfolioService } from 'portfolio/PortfolioService';
import { IndigoStore } from 'store/store';
import { LoggingService } from '@instinet-analytics/core-lib';
import {
    FilteredPortfolioChanged,
    PortfolioEventService,
} from 'portfolio/PortfolioEventService';
import { FilteredPortfolioChangedEventType } from 'portfolio/store/actions';
import { selectMyPortfolioCapsulesDataState } from './selectors';
import {
    myPortfolioCapsulesClearAction,
    myPortfolioCapsulesDataSetAction,
} from './actions';
import {
    applyChanges,
    computeFullAggregatedData,
} from './MyPortfolioCapsulesDataAggregator';
import { selectAllActiveTopics } from 'widgets/myPortfolioCapsules/store/selectors';
import Measure from 'utils/measure/MeasureDecorator';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import { TopicType } from 'widgets/myPortfolioCapsules/topics/TopicType';

interface MyPortfolioCapsulesDataServiceOptions {
    loggingService: LoggingService;
    portfolioService: PortfolioService;
    portfolioEventService: PortfolioEventService;
}

export class MyPortfolioCapsulesDataService {
    private logger: Logger;
    private portfolioService: PortfolioService;
    public store: IndigoStore | undefined;

    public constructor(options: MyPortfolioCapsulesDataServiceOptions) {
        this.logger = buildLogger(
            'MyPortfolioCapsulesDataService',
            options.loggingService
        );
        this.portfolioService = options.portfolioService;

        options.portfolioEventService.subscribeFilteredPortfolioChanged(
            this.onFilteredPortfolioChangedAsync.bind(this)
        );
    }

    @Measure()
    public recalculate() {
        if (!this.store) {
            throw new Error('store must be set');
        }

        const topics = selectAllActiveTopics(this.store.getState());
        if (topics.length === 0) {
            this.logger.debug('No topics to aggregate');
            this.store.dispatch(myPortfolioCapsulesClearAction());
            return;
        }

        this.logger.debug(
            'Aggregating filtered portfolio for topics ' + topics.join(', ')
        );

        const filteredPortfolio = this.portfolioService.getFilteredPortfolio();

        const nextData = this.computeFullAggregatedData(
            filteredPortfolio,
            topics
        );

        this.logger.debug(
            'Aggregated filtered portfolio for topics ' + topics.join(', ')
        );
        this.store.dispatch(myPortfolioCapsulesDataSetAction(nextData));
        this.logger.debug(
            'Dispatched aggregated filtered portfolio for topics ' +
                topics.join(', ')
        );
    }

    private computeFullAggregatedData(
        filteredPortfolio: PortfolioItem[],
        topics: TopicType[]
    ) {
        return computeFullAggregatedData(
            this.logger,
            filteredPortfolio,
            topics
        );
    }

    private onFilteredPortfolioChangedAsync(ev?: FilteredPortfolioChanged) {
        setTimeout(() => this.onFilteredPortfolioChanged(ev), 0);
    }

    private onFilteredPortfolioChanged(ev?: FilteredPortfolioChanged) {
        if (!ev) {
            return;
        }

        if (!this.store) {
            throw new Error('store must be set');
        }

        const topics = selectAllActiveTopics(this.store.getState());

        if (topics.length === 0) {
            return;
        }

        if (ev.changedType === FilteredPortfolioChangedEventType.RESET) {
            this.recalculate();
            return;
        }

        if (ev.batchUpdateInProgress) {
            this.logger.debug(
                'Ignoring incremental change as batch update is in progress'
            );
            return;
        }

        const state = this.store.getState();

        const data = selectMyPortfolioCapsulesDataState(state);
        const nextData = applyChanges(ev, data, topics);

        this.store.dispatch(myPortfolioCapsulesDataSetAction(nextData));
    }
}
